import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { getMetrologos } from "../../api/common";

// Bogotá es UTC-5 fijo (Colombia no usa DST). Hardcodear el offset evita la dependencia moment-timezone.
const BOGOTA_OFFSET_MS = 5 * 60 * 60 * 1000;

// ISO UTC → "YYYY-MM-DDTHH:mm" en hora Bogotá (para alimentar inputs datetime-local)
const utcIsoToBogotaInput = (iso) => {
  if (!iso) return "";
  const utcMs = new Date(iso).getTime();
  if (Number.isNaN(utcMs)) return "";
  return new Date(utcMs - BOGOTA_OFFSET_MS).toISOString().slice(0, 16);
};

// "YYYY-MM-DDTHH:mm" del input (asumido en Bogotá) → ISO con offset explícito -05:00
const bogotaInputToIso = (naive) => {
  if (!naive) return null;
  return `${naive}:00-05:00`;
};

/**
 * Formulario para reprogramar una Programación o una OT.
 * Props:
 *   onAceptar(data)  — llamado con { fechaInicio, fechaCierre, responsablesId }
 *   onCancelar()     — cancelar sin guardar
 *   visitaActual     — objeto { fechaInicio, fechaFin, responsablesId[] } para pre-poblar (opcional)
 *   metrologos       — array { value, label } para el selector multi
 */
const ReprogramarProgramacion = ({ onAceptar, onCancelar, visitaActual }) => {
  const [metrologos, setMetrologos] = useState([]);

  const [formData, setFormData] = useState({ fechaInicio: "", fechaCierre: "" });
  const [fechaInicioOriginal, setFechaInicioOriginal] = useState("");
  const [selectedResponsables, setSelectedResponsables] = useState([]);

  // Pre-poblar desde visitaActual si llega
  useEffect(() => {
    if (!visitaActual) return;

    const fi = utcIsoToBogotaInput(visitaActual.fechaInicio);
    const fc = utcIsoToBogotaInput(visitaActual.fechaFin);

    setFormData({ fechaInicio: fi, fechaCierre: fc });
    setFechaInicioOriginal(fi);

    if (Array.isArray(visitaActual.responsablesId) && visitaActual.responsablesId.length > 0) {
      const preSelec = visitaActual.responsablesId.map((r) => ({
        value: r._id || r,
        label: r.nombre ? `${r.nombre} ${r.apellido || ""}`.trim() : String(r._id || r),
      }));
      setSelectedResponsables(preSelec);
    }
  }, [visitaActual]);

    // Cargar metrólogos una sola vez al montar
    useEffect(() => {
      getMetrologos()
        .then((res) => {
          console.log('Respuesta de getMetrologos:', res); // Debug: revisar respuesta de la API
          const lista = (res?.data.data || []).map((m) => ({
            value: m._id,
            label: `${m.nombre} ${m.apellido || ''}`.trim(),
          }));
          setMetrologos(lista);
        })
        .catch((err) => console.error('[ModalReprogramar] Error cargando metrólogos:', err));
    }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Piso base: no se puede reprogramar a un momento anterior a "ahora - 5h" en hora Bogotá.
  // Si la visita original ya estaba antes de ese piso (visita pasada que solo se quiere
  // extender), la propia fecha original se vuelve el piso — el usuario puede mantenerla
  // pero no moverla aún más hacia atrás.
  const MARGEN_REPROGRAMACION_MS = 5 * 60 * 60 * 1000;
  const minFechaInicioBase = utcIsoToBogotaInput(new Date(Date.now() - MARGEN_REPROGRAMACION_MS).toISOString());
  const minFechaInicio = fechaInicioOriginal && fechaInicioOriginal < minFechaInicioBase
    ? fechaInicioOriginal
    : minFechaInicioBase;
  
  // min para fechaCierre: igual a fechaInicio seleccionada (o el mínimo si no hay)
  const minFechaCierre = formData.fechaInicio || minFechaInicio;

  const handleSubmit = (e) => {
    e.preventDefault();

    const { fechaInicio, fechaCierre } = formData;

    if (!fechaInicio || !fechaCierre) {
      Swal.fire("Campos incompletos", "Por favor complete todos los campos.", "warning");
      return;
    }

    // Comparación lexicográfica funciona porque datetime-local entrega "YYYY-MM-DDTHH:mm"
    if (fechaInicio < minFechaInicio) {
      Swal.fire(
        "Fecha inválida",
        `La fecha de inicio no puede ser anterior a ${minFechaInicio.replace('T', ' ')}.`,
        "error"
      );
      return;
    }

    if (fechaCierre <= fechaInicio) {
      Swal.fire(
        "Rango inválido",
        "La fecha y hora de cierre deben ser posteriores a la fecha y hora de inicio.",
        "error"
      );
      return;
    }

    if (onAceptar) {
      onAceptar({
        // Enviar ISO con offset Bogotá explícito para que el backend (UTC en Heroku)
        // parsee el momento correcto sin importar su zona horaria local.
        fechaInicio: bogotaInputToIso(fechaInicio),
        fechaCierre: bogotaInputToIso(fechaCierre),
        responsablesId: selectedResponsables.map((r) => r.value),
      });
    }
  };

  return (
    <div className="p-3 bg-light rounded-3 shadow-sm">
      <h5 className="mb-3 text-primary">Reprogramar</h5>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>

              {/** si ya formData tiene una fecha ya anterior a  minFechaInicio se respeta esa fecha */}
              <Form.Label>Fecha y Hora de Inicio</Form.Label>
              <Form.Control
                type="datetime-local"
                name="fechaInicio"
                value={formData.fechaInicio}
                onChange={handleChange}
                min={minFechaInicio}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Fecha y Hora de Cierre</Form.Label>
              <Form.Control
                type="datetime-local"
                name="fechaCierre"
                value={formData.fechaCierre}
                onChange={handleChange}
                min={minFechaCierre}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Responsables de la visita</Form.Label>
          <Select
            isMulti
            options={metrologos}
            value={selectedResponsables}
            onChange={setSelectedResponsables}
            placeholder="Seleccionar metrólogos..."
            noOptionsMessage={() => "Sin opciones"}
          />
        </Form.Group>

        <div className="d-flex justify-content-end mt-3">
          <Button variant="secondary" className="me-2" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit">
            Aceptar
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ReprogramarProgramacion;
